import { Checkbox } from '@/components/ui/checkbox';
import type { AlertRule, AlertRulePatch } from '@/lib/adminApi';
import { AlertNumberKnob } from './AlertNumberKnob';

const CHECKBOX_CLASS = 'data-[state=checked]:bg-[#8FB560] data-[state=checked]:border-[#8FB560]';

/**
 * The "Low or critical battery on a trailer" rule's two follow-up notices (Eric: "add it
 * (both options, configurable for all or some options)"). Each has its own switch
 * and value; one that is off keeps its value for when it is switched back on, and
 * the value stays editable: a lower level left above a since-lowered critical
 * line must be fixable before it can be switched back on.
 *   - Repeat to everyone every N minutes while nobody owns the alert.
 *   - Alert again once the battery drops below a lower level (below the critical
 *     line): everyone if nobody owns it, else the owner.
 */
export function CriticalBatteryFollowUps({ rule, saving, version, onPatch }: {
  rule: AlertRule;
  saving: boolean;
  /** Bumped by the panel after a rejected save, to show the saved values again. */
  version: number;
  onPatch: (patch: AlertRulePatch) => void;
}) {
  const reminderOn = rule.reminderEnabled !== false;
  const deeperOn = rule.deeperEnabled !== false;
  const critical = rule.threshold ?? 30;

  return (
    <div className="w-full border-t border-border pt-3 space-y-2">
      <p className="text-xs font-medium text-muted-foreground">Follow-up notices</p>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Checkbox
            checked={reminderOn}
            disabled={saving}
            onCheckedChange={value => onPatch({ reminderEnabled: value === true })}
            className={CHECKBOX_CLASS}
          />
          Repeat while nobody owns it
        </label>
        <AlertNumberKnob
          label="Every (minutes)"
          inputKey={`${rule.key}-reminder-${rule.reminderMinutes}-${version}`}
          value={rule.reminderMinutes ?? 60}
          min={15}
          max={720}
          disabled={saving}
          onSave={value => onPatch({ reminderMinutes: value })}
        />
      </div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Checkbox
            checked={deeperOn}
            disabled={saving}
            onCheckedChange={value => onPatch({ deeperEnabled: value === true })}
            className={CHECKBOX_CLASS}
          />
          Critical battery alert at a lower level
        </label>
        <AlertNumberKnob
          label="Critical battery at or below (%)"
          inputKey={`${rule.key}-deeper-${rule.deeperThreshold}-${version}`}
          value={rule.deeperThreshold ?? 15}
          min={1}
          max={critical - 1}
          disabled={saving}
          onSave={value => onPatch({ deeperThreshold: value })}
        />
      </div>
    </div>
  );
}
